// AES-KW wrap/unwrap of DEK with KEK_protocolo (D-22, CRY-02).
// KEK is the second 32 bytes from Argon2id derivation (kekRaw from argon2id.ts).
// AES-KW output is 40 bytes for a 32-byte input (RFC 3394: 32 + 8 bytes checksum).

// TS 6.x: crypto.subtle requires ArrayBuffer, not ArrayBufferLike.
function toBuffer(arr: Uint8Array): ArrayBuffer {
  return arr.buffer.slice(arr.byteOffset, arr.byteOffset + arr.byteLength) as ArrayBuffer;
}

/**
 * AES-KW wrap of DEK using 32-byte raw KEK.
 * Output is 40 bytes (RFC 3394: input + 8 bytes integrity check).
 */
export async function wrapDek(dekRaw: Uint8Array, kekRaw: Uint8Array): Promise<Uint8Array> {
  // Import KEK as AES-KW wrapping key
  const kek = await crypto.subtle.importKey('raw', toBuffer(kekRaw), { name: 'AES-KW' }, false, [
    'wrapKey',
  ]);

  // Import DEK as a CryptoKey so we can wrap it
  // extractable=true required by AES-KW spec; the KEY itself never leaves browser memory.
  const dekKey = await crypto.subtle.importKey(
    'raw',
    toBuffer(dekRaw),
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );

  const wrapped = await crypto.subtle.wrapKey('raw', dekKey, kek, 'AES-KW');
  return new Uint8Array(wrapped);
}

/**
 * AES-KW unwrap of DEK using 32-byte raw KEK.
 * Returns the unwrapped DEK as raw bytes.
 */
export async function unwrapDek(wrappedDek: Uint8Array, kekRaw: Uint8Array): Promise<Uint8Array> {
  const kek = await crypto.subtle.importKey('raw', toBuffer(kekRaw), { name: 'AES-KW' }, false, [
    'unwrapKey',
  ]);

  const dekKey = await crypto.subtle.unwrapKey(
    'raw',
    toBuffer(wrappedDek),
    kek,
    'AES-KW',
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );

  const exported = await crypto.subtle.exportKey('raw', dekKey);
  return new Uint8Array(exported);
}
